import _ from 'lodash'
import React, { useContext, useMemo, useState } from 'react'
import { Icon } from '@blueprintjs/core'

import { FormFocusTargetContext } from 'shared-libs/components/ui2/behavior/form-focus-target'

import './_entity-error-summary-banner.scss'

interface IEntityErrorSummaryBannerProps {
  /**
   * UI-driven required-field errors reported by the renderer via `onValidate`.
   * Keys are dot-joined value paths (e.g. `address.streetAddress`); values are
   * arrays of error message strings.
   */
  uiErrors?: Record<string, string[]>
  /**
   * Optional map from valuePath → UI label. When present the banner shows
   * the label ("Required Test") for that entry instead of falling back to
   * a schema-derived name or the raw path. Emitted by the renderer
   * alongside `uiErrors`.
   */
  uiErrorLabels?: Record<string, string>
  /**
   * Server / schema validation errors. Same shape as `uiErrors` — keys are
   * dot-joined paths (when keyed) or generic fallback messages.
   */
  errors?: Record<string, any> | null
  /**
   * Optional entity used to resolve field paths to human-readable labels.
   * Matches the behavior of EntityErrorBlock.
   */
  entity?: any
  /**
   * When false, the banner is hidden even if errors exist. Save handlers flip
   * this to true on submit so the banner doesn't appear on initial load.
   */
  isVisible?: boolean
  className?: string
}

interface IErrorEntry {
  key: string         // valuePath like `address.streetAddress`, or '' for unkeyed
  label?: string      // resolved schema label, falls back to key
  messages: string[]  // one or more error messages for this entry
  source: 'ui' | 'server'
}

/**
 * Collapsible red banner that summarizes every active error on a form — both
 * UI-driven required-field errors from the renderer and schema/server errors
 * coming back from save. Mirrors the NM `EntityErrorSheet` pattern so the
 * "N validation issues" affordance is consistent across platforms.
 *
 * Tap the header to expand/collapse. Tap an individual item to scroll the
 * page to the offending field (uncollapsing any collapsible section the
 * field is hidden inside).
 */
export const EntityErrorSummaryBanner: React.FC<IEntityErrorSummaryBannerProps> = ({
  uiErrors,
  uiErrorLabels,
  errors,
  entity,
  isVisible = true,
  className,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const focusContext = useContext(FormFocusTargetContext)

  const entries = useMemo<IErrorEntry[]>(
    () => buildEntries(uiErrors, uiErrorLabels, errors, entity),
    [uiErrors, uiErrorLabels, errors, entity],
  )

  if (!isVisible || entries.length === 0) return null

  const totalCount = _.sumBy(entries, (e) => e.messages.length)
  const title = `${totalCount} validation issue${totalCount === 1 ? '' : 's'}`

  return (
    <div className={`c-errorSummaryBanner ${className ?? ''}`}>
      <button
        type='button'
        className='c-errorSummaryBanner-header'
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((v) => !v)}
      >
        <div className='c-errorSummaryBanner-headerLeft'>
          <Icon icon='warning-sign' iconSize={16} />
          <span className='c-errorSummaryBanner-title'>{title}</span>
        </div>
        <Icon icon={isExpanded ? 'chevron-up' : 'chevron-down'} iconSize={16} />
      </button>
      {isExpanded && (
        <ul className='c-errorSummaryBanner-list'>
          {entries.map((entry, idx) => (
            <li key={`${entry.source}:${entry.key}:${idx}`} className='c-errorSummaryBanner-item'>
              <button
                type='button'
                className='c-errorSummaryBanner-itemButton'
                onClick={() => { void focusContext.focusPath(entry.key) }}
                disabled={!entry.key}
              >
                <span className='c-errorSummaryBanner-itemLabel'>{entry.label || 'Error'}</span>
                <span className='c-errorSummaryBanner-itemMessage'>{entry.messages.join('; ')}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function buildEntries(
  uiErrors: Record<string, string[]> | undefined,
  uiErrorLabels: Record<string, string> | undefined,
  errors: Record<string, any> | null | undefined,
  entity: any,
): IErrorEntry[] {
  const result: IErrorEntry[] = []
  const seenKeys = new Set<string>()

  _.forEach(uiErrors ?? {}, (messages, key) => {
    const cleanMessages = _.castArray(messages).filter((m) => !_.isEmpty(m)) as string[]
    if (_.isEmpty(cleanMessages)) return
    seenKeys.add(key)
    result.push({
      key,
      label: uiErrorLabels?.[key] || resolveLabel(entity, key) || key,
      messages: cleanMessages,
      source: 'ui',
    })
  })

  _.forEach(errors ?? {}, (raw, key) => {
    if (seenKeys.has(key)) return
    const messages = normalizeMessages(raw)
    if (_.isEmpty(messages)) return
    const isUnkeyed = _.isEmpty(key) || key === 'undefined'
    result.push({
      key: isUnkeyed ? '' : key,
      label: isUnkeyed
        ? undefined
        : uiErrorLabels?.[key] || resolveLabel(entity, key) || key,
      messages,
      source: 'server',
    })
  })

  return result
}

function resolveLabel(entity: any, key: string): string | undefined {
  if (!entity || !key) return undefined
  try {
    const path = key.split('.')
    const sub = entity.resolveSubschemaByValuePath?.(path)
    return sub?.schema?.label
  } catch {
    return undefined
  }
}

function normalizeMessages(raw: any): string[] {
  if (_.isString(raw)) return [raw]
  if (_.isArray(raw)) {
    return _.compact(
      raw.map((m) => (_.isString(m) ? m : m?.message || m?.detail || (m && JSON.stringify(m)))),
    )
  }
  if (raw?.message) return [String(raw.message)]
  return []
}
